import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useEncounterCreationGate } from "../hooks/use-encounter-creation-gate";
import {
  AppointmentControllerApi,
  AppointmentStatusEnum,
  GetAllAppointmentsFilterEnum,
} from "../services";
import { AppointmentDetails } from "../types/appointment-types";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../utils/utils";

interface UsePatientsListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  patientUuid?: string;
  providerUuid?: Array<string>;
  startDate?: string;
  endDate?: string;
  filter?: GetAllAppointmentsFilterEnum;
  searchKey?: string;
  statusKey?: boolean;
  archieve?: boolean;
  providerGroupId?: string | null;
  locationUuid?: Array<string>;
  appointmentType?: string;
  appointmentMode?: string;
  appointmentStatus?: AppointmentStatusEnum;
  archive?: boolean;
  notForBilling?: boolean;
  disabled?: boolean;
  superBillGenerated?: boolean;
  appStatusForPatient?: string;
}

/**
 *
 * @author Shrinivas Mandhare
 */
export function useAppointmentsList(
  props: UsePatientsListProps
): useBaseContent<AppointmentDetails> {
  const prevPage = useRef<number>(props.pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination: props.pagination,
    searchKey: props.searchKey,
    prevPage: prevPage,
  });

  const pageSize = getPageSize(props.searchKey, props.pagination);
  const sortData = props.gridSorting?.[0];
  const skip = useEncounterCreationGate();

  return useQuery({
    queryKey: [
      "appointment-list",
      pageSize,
      pageIndex,
      props,
      props.gridSorting,
    ],
    enabled: !!props.providerGroupId && !props.disabled && !skip,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,

    queryFn: async () =>
      new AppointmentControllerApi(getConfigObject())
        .getAllAppointments(
          pageIndex, //page
          pageSize, //size
          sortData?.field ?? undefined, // sortBy
          sortData?.sort ?? undefined, // sortDirection
          props.patientUuid, //patientUuid
          props.providerUuid, //providerUuid
          props.locationUuid, //locationUuid
          props.filter, //filter
          props.appointmentType, //appointmentType
          props.appointmentMode, //appointmentMode
          props.appointmentStatus, //appointmentStatus
          props?.appStatusForPatient, //appStatusForPatient
          props.startDate, //startDate
          props.notForBilling,
          props.superBillGenerated, //superBillGenerated
          props.archieve, //archieve
          props.endDate, //endDate
          props.searchKey, //searchKey
          props.providerGroupId! //xTENANTID
        )
        .then((it) => asPaginatedContent<AppointmentDetails>(it?.data)),
  });
}
